import React from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { MetricCard } from '../components/growth/MetricCard';
import { KPIGauge } from '../components/growth/KPIGauge';
import { PerformanceTrendChart } from '../components/growth/PerformanceTrendChart';
import { LeadSourceBreakup } from '../components/growth/LeadSourceBreakup';
import { LeadTypeOverview } from '../components/growth/LeadTypeOverview';
import { LeadQueryOverview } from '../components/growth/LeadQueryOverview';
import { FloatingCart } from '../components/billing/FloatingCart';
import { AllLocationsPlaceholder } from '../components/AllLocationsPlaceholder';
import { useViewMode } from '../context/ViewModeContext';
import { MapPinIcon } from 'lucide-react';
import { BRAND_METRICS, BRAND_KPIS, LOCATION_DATA } from '../data/mockData';
export function GrowthConsole() {
  const {
    viewMode,
    currentLocation,
    isAllLocationsSelected,
    isSingleLocationSelected,
    selectedBrandLocations,
    getSelectedLocationData
  } = useViewMode();
  // Get metrics based on view mode and selected location
  const getMetrics = () => {
    if (viewMode === 'store' && currentLocation) {
      return LOCATION_DATA[currentLocation.code]?.metrics || BRAND_METRICS;
    }
    if (viewMode === 'brand') {
      if (isSingleLocationSelected) {
        const locationCode = selectedBrandLocations[0];
        return LOCATION_DATA[locationCode]?.metrics || BRAND_METRICS;
      }
      return BRAND_METRICS;
    }
    return BRAND_METRICS;
  };
  const getKpis = () => {
    if (viewMode === 'store' && currentLocation) {
      return LOCATION_DATA[currentLocation.code]?.kpis || BRAND_KPIS;
    }
    if (viewMode === 'brand') {
      if (isSingleLocationSelected) {
        const locationCode = selectedBrandLocations[0];
        return LOCATION_DATA[locationCode]?.kpis || BRAND_KPIS;
      }
      return BRAND_KPIS;
    }
    return BRAND_KPIS;
  };
  const metrics = getMetrics();
  const kpis = getKpis();
  const selectedLocation = getSelectedLocationData();
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-3056">
      <Sidebar data-id="element-3057" />
      <main className="flex-1" data-id="element-3058">
        <Header data-id="element-3059" />
        <div className="px-6 py-6" data-id="element-3060">
          <div className="flex items-center justify-between mb-6" data-id="element-3061">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3062">
              Growth Console
            </h1>
            {viewMode === 'store' && currentLocation && <span className="inline-flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium border border-emerald-200" data-id="element-3063">
                <MapPinIcon size={16} className="mr-2" data-id="element-3064" />
                {currentLocation.name} ({currentLocation.code})
              </span>}
            {viewMode === 'brand' && isAllLocationsSelected && <span className="text-sm text-gray-500" data-id="element-3065">
                Aggregated data across all 5 locations
              </span>}
            {viewMode === 'brand' && isSingleLocationSelected && selectedLocation && <span className="inline-flex items-center px-4 py-2 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium border border-amber-200" data-id="element-3066">
                  <MapPinIcon size={16} className="mr-2" data-id="element-3067" />
                  Filtered: {selectedLocation.name} ({selectedLocation.code})
                </span>}
          </div>

          {/* Top Metrics Cards */}
          <div className="grid grid-cols-5 gap-4 mb-6" data-id="element-3068">
            <MetricCard label="Spend" value={metrics.spend} data-id="element-3069" />
            <MetricCard label="CPC" value={metrics.cpc} data-id="element-3070" />
            <MetricCard label="CTR" value={metrics.ctr} data-id="element-3071" />
            <MetricCard label="Leads" value={metrics.leads} data-id="element-3072" />
            <MetricCard label="CPL" value={metrics.cpl} data-id="element-3073" />
          </div>

          {/* KPI Gauges */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mb-6" data-id="element-3074">
            <h2 className="text-base font-semibold text-gray-900 mb-6" data-id="element-3075">KPI</h2>
            <div className="grid grid-cols-4 gap-8" data-id="element-3076">
              <KPIGauge label="Call Answered Ratio" percentage={kpis.callAnsweredRatio.percentage} value={kpis.callAnsweredRatio.value} color="#2563eb" data-id="element-3077" />
              <KPIGauge label="Call Leads with Status" percentage={kpis.callLeadsWithStatus.percentage} value={kpis.callLeadsWithStatus.value} color="#2563eb" data-id="element-3078" />
              <KPIGauge label="Call Leads Sync" percentage={kpis.callLeadsSync.percentage} value={kpis.callLeadsSync.value} color="#2563eb" data-id="element-3079" />
              <KPIGauge label="Enquiry to Booking Ratio" percentage={kpis.enquiryToBooking.percentage} value={kpis.enquiryToBooking.value} color={kpis.enquiryToBooking.percentage < 10 ? '#ef4444' : '#2563eb'} data-id="element-3080" />
            </div>
          </div>

          {/* Performance Trend Chart - Only for All Locations */}
          <div className="mb-6" data-id="element-3081">
            {viewMode === 'brand' && isSingleLocationSelected ? <AllLocationsPlaceholder title="Performance Trend Chart" description="Performance trend analysis is available when viewing all locations aggregated data." data-id="element-3082" /> : <PerformanceTrendChart data-id="element-3083" />}
          </div>

          {/* Bottom Row: Lead Source, Lead Type, Lead Query */}
          <div className="grid grid-cols-3 gap-6 mb-6" data-id="element-3084">
            <div className="col-span-2" data-id="element-3085">
              <LeadSourceBreakup data-id="element-3086" />
            </div>
            <LeadTypeOverview data-id="element-3087" />
          </div>

          {/* Lead Query Overview - Only for All Locations */}
          {viewMode === 'brand' && isSingleLocationSelected ? <AllLocationsPlaceholder title="Lead Query Overview" description="Lead query analysis across all sources is available when viewing all locations." data-id="element-3088" /> : <LeadQueryOverview data-id="element-3089" />}
        </div>
      </main>

      {/* Floating Cart */}
      <FloatingCart data-id="element-3090" />
    </div>;
}